import { axiosApi } from './api'
import groupBy from 'lodash/groupBy'
import { providesList } from 'utils/rtkHelpers'
import { transformPatientFiles } from 'utils/transform'

const type = 'Patient'

export const patient = axiosApi.injectEndpoints({
    endpoints: build => ({
        /*
         * GET list of patients under an agreement
         */
        getPatients: build.query({
            query: ({ page = 1, pageSize = 20 }) =>
                `v1/patients?Page.number=${page}&Page.Size=${pageSize}`,
            transformResponse: res => res.data,
            providesTags: result => providesList(result, type, 'id'),
            keepUnusedDataFor: 3600,
        }),

        /*
         * GET files of a patient by patient id
         */
        getPatientStudyFiles: build.query({
            query: ({ id, query = '' }) => `v1/patients/${id}/files?${query}`,
            transformResponse: res => {
                return {
                    dates: [...new Set(res.data.map(n => n.series.acquisitionDate))].sort(
                        (a, b) => new Date(b) - new Date(a)
                    ),
                    studies: groupBy(res.data, n => n.series.acquisitionDate),
                    files: res.data,
                }
            },
            keepUnusedDataFor: 1200,
        }),

        /*
         * GET files and summary of a patient by patient id
         */
        getPatientFilesWithSummary: build.query({
            async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
                const summary = await fetchWithBQ(
                    `v1/patients/${_arg.id}/files?Summary=count&${_arg.query}`
                )
                if (summary.error) throw summary.error
                const count = summary.data.summary

                if (count.all) {
                    const result = await fetchWithBQ(`v1/patients/${_arg.id}/files?${_arg.query}`)
                    if (!result.data) {
                        return { error: result.error }
                    }
                    const transformedData = transformPatientFiles(result.data.data)
                    return {
                        data: {
                            dates: [...new Set(transformedData.map(n => n.series.acquisitionDate))],
                            studies: groupBy(transformedData, n => n.series.acquisitionDate),
                            files: transformedData,
                            summary: count,
                        },
                    }
                }
                return {
                    data: {
                        dates: [],
                        studies: {},
                        files: [],
                        summary: count,
                    },
                }
            },
            providesTags: result => (result ? ['PatientFiles'] : null),
            keepUnusedDataFor: 1200,
        }),

        /*
         * GET auto suggestion for patients search.
         */
        getPatientsSuggestions: build.query({
            async queryFn({ keyword, query = '' }, _queryApi, _extraOptions, fetchWithBQ) {
                const res = await fetchWithBQ(
                    `v1/patients?Query=patientname=co:"${keyword}"&${query}`
                )
                if (res.error) throw res.error
                const patients = res.data.data

                const result = await fetchWithBQ(
                    `v1/patients/_tags?Query=tags=co:"${keyword}"&${query}`
                )

                const tags = result.data.data
                const response = []
                if (patients.length > 0) {
                    response.push({
                        type: 'user',
                        data: patients.map(p => {
                            return {
                                ...p,
                                name: `${p.lastName}, ${p.firstName}`,
                            }
                        }),
                    })
                }
                if (tags.length > 0) {
                    response.push({
                        type: 'tag',
                        data: tags.map((t, i) => {
                            return {
                                id: i + 1,
                                name: t,
                            }
                        }),
                    })
                }
                return {
                    data: response,
                }
            },
            transformResponse: res => res.data,
        }),

        /*
         * GET summary of patient files.
         * Mainly used with share feature to get the total files under a patient.
         */
        getPatientsSummary: build.query({
            query: ({ id, query = '' }) => `v1/patients/${id}/files?Summary=count&${query}`,
            transformResponse: res => res.summary,
            keepUnusedDataFor: 600,
        }),

        /*
         * DELETE patient files.
         */
        deletePatientFiles: build.mutation({
            query: ({ id, query }) => ({
                url: `v1/patients/${id}/files?${query}`,
                method: 'DELETE',
            }),
            transformResponse: res => res.data,
        }),
    }),
    overrideExisting: false,
})

export const {
    useGetPatientsQuery,
    useGetPatientStudyFilesQuery,
    useGetPatientsSuggestionsQuery,
    useGetPatientFilesWithSummaryQuery,
    useGetPatientsSummaryQuery,
    useDeletePatientFilesMutation,
} = patient
